import React from 'react';
import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { NextSeo } from 'next-seo';
import Link from 'next/link';

const Article = () => {
  return (
    <MainLayout>
      <NextSeo
        title="Playing Fair: How We Do Prize Draws"
        description="Keeping it fun whilst looking after our players"
        openGraph={{
          title: 'Playing Fair: How We Do Prize Draws',
          description: 'Keeping it fun whilst looking after our players',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/playing-fair-how-we-do-prize-draws',
          images: [
            {
              url: `https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/blog/articles/how-we-do-prize-draws/chat-bubble-smile.jpg`,
              alt: 'A smiling chat bubble',
            },
          ],
        }}
      />
      <style>
        {`
        ul.blog2026 {
          width: 100%;
          margin-top: 0;
          margin-bottom: 0;
        }
        
      `}
      </style>
      <BlogArticle
        imageDirectory="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/blog/articles/how-we-do-prize-draws"
        heroAltText="Two hands forming a heart shape"
        heroImageYPosition="33%"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            Playing Fair: How We Do Prize Draws
          </h1>

          <p>
            At DAYMADE, doing things the right way isn't a policy, it's just how
            we're built. We're proud to be a signatory of the UK Government's
            Voluntary Code of Good Practice for Prize Draw Operators, and
            everything below shows exactly how we live up to it.
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0, marginBottom: 0 }}
          />

          <h2>Keeping It Safe</h2>
          <h3>Who can play</h3>
          <p>
            Prize draws are strictly 18+. We verify ages, and we don't market to
            under-18s.
          </p>
          <h3>Spending limits</h3>
          <p>
            We want DAYMADE to feel exciting, not stressful. So we've built in
            real safeguards:
          </p>
          <ul className="blog2026">
            <li>Monthly total spend capped at £120 per customer </li>
            <li>You can set your own limit, including £0</li>
            <li>We do not allow credit-card payments</li>
          </ul>
          <h3>Taking a break</h3>
          <p>
            Need to step back? You can snooze, suspend (minimum 6 months) or
            close your account anytime. When your account is suspended, you will
            not receive any marketing communication and won’t be able to enter
            any draws.
          </p>
          <h3>Spotting problems early</h3>
          <p>
            We monitor participation patterns. If something looks off, we act
            proportionately and promptly. If we think you might need support, we
            signpost you to responsible play information and relevant support
            resources.
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0, marginBottom: 0 }}
          />

          <h2>Transparency You Can Actually See</h2>
          <h3>Fair draws, every time</h3>
          <ul className="blog2026">
            <li>Free and paid entries have identical odds</li>
            <li>
              The free entry route is visible and accessible before entering
            </li>
            <li>
              Winners are selected via a verifiably random, auditable process
            </li>
            <li>
              Draw mechanics are explained on our site on our T&C’s page as well
              as our How It Works page
            </li>
            <li>
              Rules and entry rules are explained clearly on our site before
              entering
            </li>
            <li>
              We do not offer instant-win games, so there is always appropriate
              time between a draw opening and concluding
            </li>
          </ul>
          <h3>Prizes we stand behind</h3>
          <p>
            We don't cancel draws, move goalposts or downgrade prizes because
            ticket sales are slow. What we advertise is what you play for. We
            deliver prizes won as advertised promptly.
          </p>
          <h3>Honest marketing</h3>
          <p>
            We follow CAP and BCAP Codes. We'll never suggest prize draws are a
            financial strategy or a way out of hardship.
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0, marginBottom: 0 }}
          />

          <h2>Our Charitable Commitments</h2>
          <p>
            We're transparent about exactly how we support our partner charities
            and how much. We publish quarterly certificates with our charity
            contributions on our ‘Plant Trees’ page. We're registered with the
            Fundraising Regulator and follow their rules.
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0, marginBottom: 0 }}
          />

          <h2>If Something Goes Wrong</h2>
          <p>
            We have a clear complaints process and a dispute resolution route.
            And if you believe any operator - including us - isn't meeting the
            Code's standards, you can raise this with the relevant organisation
            or contact DCMS directly at prizedrawcode@dcms.gov.uk.
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0, marginBottom: 0 }}
          />

          <h2>Always Improving</h2>
          <p>
            We go beyond what the Voluntary Code of Good Practice requires. This
            includes for example having the lowest spending cap in the industry.
            We believe prize draws should be fun, fair and inclusive. As such,
            we set an industry-standard and will make sure to engage with other
            operators to share best practices in relation to player protections,
            transparency and accountability. See our Responsible Play page for
            more.
          </p>
          <p>
            Compliance isn't a badge we hang on the wall. We actively review our
            processes, fix gaps fast, and hold our third-party partners (e.g.
            affiliates, charity partners) to the same standards through
            contractual requirements. We are committed to working with DCMS to
            help the Code evolve and will share any learnings or challenges.
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0, marginBottom: 16 }}
          />

          <p>
            More information can be found on our{' '}
            <Link href="/responsible-play">
              Responsible Play
            </Link>{' '}
            page.
          </p>

          <p>Last reviewed: May 2026. Updated regularly.</p>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
